const FX_CACHE_KEY = "fx-rate-jpy-usd";
const FX_CACHE_TTL_MS = 1000 * 60 * 60; // 1 hour

const safeStorage =
  typeof window !== "undefined" ? window.sessionStorage : null;

const readCache = () => {
  if (!safeStorage) return null;
  try {
    const raw = safeStorage.getItem(FX_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeCache = (payload) => {
  if (!safeStorage) return;
  try {
    safeStorage.setItem(FX_CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota errors */
  }
};

// Google Finance JPY->USD quote page (HTML; CORS may block in some environments)
const GOOGLE_FINANCE_JPY_USD = "https://www.google.com/finance/quote/JPY-USD?hl=en";
// Frankfurter remains as a safety fallback (JSON, CORS-friendly)
const FRANKFURTER_ENDPOINT = "https://api.frankfurter.app/latest?from=JPY&to=USD&amount=1";

const FALLBACK_RATE = 0.0065; // in case the API is unavailable

async function fetchFromGoogle() {
  // Fetch HTML and parse the displayed rate (e.g., 0.0067 USD)
  const res = await fetch(GOOGLE_FINANCE_JPY_USD, {
    // Rely on browser defaults; CORS may block in some deployments
    headers: { Accept: "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8" },
  });
  if (!res.ok) {
    throw new Error(`Google rate fetch failed: ${res.status}`);
  }
  const html = await res.text();
  // Try a few patterns that commonly appear on the page
  // 1) data-last-price="0.0067"
  let m = html.match(/data-last-price="([0-9]*\.?[0-9]+)"/);
  if (!m) {
    // 2) aria-label="0.0067 United States Dollar"
    m = html.match(/aria-label="([0-9]*\.?[0-9]+)\s+United States Dollar"/i);
  }
  if (!m) {
    // 3) Generic number near JPY-USD header
    m = html.match(/JPY-USD[^<]*<\/div>[^<]*<div[^>]*>([0-9]*\.?[0-9]+)</i);
  }
  const rate = m ? Number(m[1]) : NaN;
  if (!Number.isFinite(rate) || rate <= 0) {
    throw new Error("Google rate parse failed");
  }
  return rate;
}

async function fetchFromFrankfurter() {
  const res = await fetch(FRANKFURTER_ENDPOINT, { headers: { Accept: "application/json" } });
  if (!res.ok) {
    throw new Error(`Frankfurter rate fetch failed: ${res.status}`);
  }
  const data = await res.json();
  const rate = data?.rates?.USD;
  if (typeof rate !== "number" || !Number.isFinite(rate) || rate <= 0) {
    throw new Error("Frankfurter missing USD rate");
  }
  return rate;
}

export async function fetchJpyUsdRate() {
  const cached = readCache();
  const now = Date.now();
  if (
    cached &&
    typeof cached.rate === "number" &&
    Number.isFinite(cached.rate) &&
    cached.rate > 0 &&
    typeof cached.timestamp === "number" &&
    now - cached.timestamp < FX_CACHE_TTL_MS
  ) {
    return cached.rate;
  }

  try {
    // Prefer Google Finance direct rate (no buffer applied)
    let rate = await fetchFromGoogle();
    // If Google was blocked or failed to parse, fall back to Frankfurter
    if (!Number.isFinite(rate) || rate <= 0) {
      rate = await fetchFromFrankfurter();
    }
    writeCache({ rate, timestamp: now });
    return rate;
  } catch {
    // Try fallback provider once if the primary path failed completely
    try {
      const rate = await fetchFromFrankfurter();
      writeCache({ rate, timestamp: now });
      return rate;
    } catch {
      if (cached && typeof cached.rate === "number" && cached.rate > 0) {
        return cached.rate;
      }
      return FALLBACK_RATE;
    }
  }
}

// ── USD -> arbitrary currency ──────────────────────────────────────────────
// Used by the header's currency selector. Google Finance is tried first
// because it is the quoted source we want, but a browser fetch of google.com
// is subject to CORS and is blocked in most deployments, so two JSON APIs
// back it up. open.er-api.com is the workhorse: one request returns every
// rate, and it covers the long tail of currencies (150 distinct across our
// ship-to list) that Frankfurter's ~30 majors do not.
const ER_API_LATEST_USD = "https://open.er-api.com/v6/latest/USD";

async function googleUsdRate(target) {
  const res = await fetch(`https://www.google.com/finance/quote/USD-${target}?hl=en`, {
    headers: { Accept: "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8" },
  });
  if (!res.ok) throw new Error(`Google rate fetch failed: ${res.status}`);
  const html = await res.text();
  const m = html.match(/data-last-price="([0-9]*\.?[0-9]+)"/);
  const rate = m ? Number(m[1]) : NaN;
  if (!Number.isFinite(rate) || rate <= 0) throw new Error("Google rate parse failed");
  return rate;
}

async function erApiUsdRate(target) {
  const res = await fetch(ER_API_LATEST_USD, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`er-api fetch failed: ${res.status}`);
  const data = await res.json();
  const rate = data?.rates?.[target];
  if (typeof rate !== "number" || !Number.isFinite(rate) || rate <= 0) {
    throw new Error(`er-api has no rate for ${target}`);
  }
  return rate;
}

async function frankfurterUsdRate(target) {
  const res = await fetch(
    `https://api.frankfurter.app/latest?from=USD&to=${target}&amount=1`,
    { headers: { Accept: "application/json" } },
  );
  if (!res.ok) throw new Error(`Frankfurter fetch failed: ${res.status}`);
  const data = await res.json();
  const rate = data?.rates?.[target];
  if (typeof rate !== "number" || !Number.isFinite(rate) || rate <= 0) {
    throw new Error(`Frankfurter has no rate for ${target}`);
  }
  return rate;
}

/**
 * How many units of `target` one USD buys.
 * Returns { rate, source }. Throws if no provider can price the currency —
 * the caller must NOT fall back to a guessed rate, because a wrong rate shows
 * every price on the site at the wrong number.
 */
export async function fetchUsdToCurrencyRate(target) {
  const code = String(target || "").trim().toUpperCase();
  if (!code) throw new Error("No target currency");
  if (code === "USD") return { rate: 1, source: "identity" };

  const providers = [
    ["google", googleUsdRate],
    ["er-api", erApiUsdRate],
    ["frankfurter", frankfurterUsdRate],
  ];
  let lastError = null;
  for (const [source, fn] of providers) {
    try {
      const rate = await fn(code);
      if (Number.isFinite(rate) && rate > 0) return { rate, source };
    } catch (err) {
      lastError = err;
    }
  }
  throw new Error(
    `No rate available for ${code}${lastError ? `: ${lastError.message}` : ""}`,
  );
}
